import { usePreviewTracks } from '@livekit/components-react'
import { Track } from 'livekit-client'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { LocalUserChoices } from '@livekit/components-react'
import type { LocalAudioTrack, LocalVideoTrack } from 'livekit-client'
import type { ReactNode } from 'react'
import type { BlockedDevice } from './DevicePermissionDialog'
import type { DeviceWarning } from './deviceWarning'
import { ReactComponent as PersonIcon } from 'assets/icons/Account.svg'

import { DeviceButton } from './DeviceButton'
import { DevicePermissionDialog } from './DevicePermissionDialog'
import { deviceWarning } from './deviceWarning'
import { refreshDeviceList } from './refreshDeviceList'
import {
  permissionWarning,
  queryDevicePermission,
  useDevicePermission
} from './useDevicePermission'
import styles from './prejoin.module.css'

const NAME_MAX_LENGTH = 60

export type PreJoinProps = {
  defaults: Pick<LocalUserChoices, 'username' | 'audioEnabled' | 'videoEnabled'>
  onSubmit: (choices: LocalUserChoices) => void
  onError?: (error: Error) => void
  children?: ReactNode
}

export function PreJoin({ defaults, onSubmit, onError, children }: PreJoinProps) {
  const { t } = useTranslation()
  const [username, setUsername] = useState(defaults.username)
  const [audioEnabled, setAudioEnabled] = useState(defaults.audioEnabled)
  const [videoEnabled, setVideoEnabled] = useState(defaults.videoEnabled)
  const [audioDeviceId, setAudioDeviceId] = useState('')
  const [videoDeviceId, setVideoDeviceId] = useState('')

  const [micWarning, setMicWarning] = useState<DeviceWarning>()
  const [cameraWarning, setCameraWarning] = useState<DeviceWarning>()
  const [blocked, setBlocked] = useState<BlockedDevice>()

  const micPermission = useDevicePermission('microphone')
  const cameraPermission = useDevicePermission('camera')

  const handleMicError = useCallback(
    (error: Error) => {
      setAudioEnabled(false)
      setMicWarning(deviceWarning('microphone', error))
      onError?.(error)
    },
    [onError]
  )

  const handleCameraError = useCallback(
    (error: Error) => {
      setVideoEnabled(false)
      setCameraWarning(deviceWarning('camera', error))
      onError?.(error)
    },
    [onError]
  )

  const toggleMic = useCallback(async () => {
    if (
      micWarning?.reason === 'blocked' &&
      (await queryDevicePermission('microphone')) === 'denied'
    ) {
      setBlocked({ device: 'microphone', warning: micWarning })

      return
    }

    setMicWarning(undefined)
    setAudioEnabled((enabled) => !enabled)
  }, [micWarning])

  const toggleCamera = useCallback(async () => {
    if (
      cameraWarning?.reason === 'blocked' &&
      (await queryDevicePermission('camera')) === 'denied'
    ) {
      setBlocked({ device: 'camera', warning: cameraWarning })

      return
    }

    setCameraWarning(undefined)
    setVideoEnabled((enabled) => !enabled)
  }, [cameraWarning])

  const audioTracks = usePreviewTracks(
    {
      audio: audioEnabled ? { deviceId: audioDeviceId || undefined } : false,
      video: false
    },
    handleMicError
  )

  const videoTracks = usePreviewTracks(
    {
      audio: false,
      video: videoEnabled ? { deviceId: videoDeviceId || undefined } : false
    },
    handleCameraError
  )

  const videoTrack = useMemo(
    () =>
      videoEnabled
        ? (videoTracks?.find((track) => track.kind === Track.Kind.Video) as
            | LocalVideoTrack
            | undefined)
        : undefined,
    [videoTracks, videoEnabled]
  )

  const audioTrack = useMemo(
    () =>
      audioEnabled
        ? (audioTracks?.find((track) => track.kind === Track.Kind.Audio) as
            | LocalAudioTrack
            | undefined)
        : undefined,
    [audioTracks, audioEnabled]
  )

  useEffect(() => {
    if (audioTrack) {
      setMicWarning(undefined)
      refreshDeviceList()
    }
  }, [audioTrack])

  useEffect(() => {
    if (videoTrack) {
      setCameraWarning(undefined)
      refreshDeviceList()
    }
  }, [videoTrack])

  useEffect(() => {
    setMicWarning((current) => permissionWarning('microphone', micPermission, current))
  }, [micPermission])

  useEffect(() => {
    setCameraWarning((current) => permissionWarning('camera', cameraPermission, current))
  }, [cameraPermission])

  const videoEl = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    if (videoEl.current && videoTrack) {
      videoTrack.unmute()
      videoTrack.attach(videoEl.current)
    }

    return () => {
      videoTrack?.detach()
    }
  }, [videoTrack])

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()

    onSubmit({ username, audioEnabled, videoEnabled, audioDeviceId, videoDeviceId })
  }

  return (
    <div className={styles.prejoin}>
      {!!blocked && <DevicePermissionDialog {...blocked} onClose={() => setBlocked(undefined)} />}

      <div className={styles.stage}>
        <div className={styles.preview}>
          {videoTrack ? (
            <video ref={videoEl} className={styles.previewVideo} muted playsInline />
          ) : (
            <div className={styles.previewPlaceholder}>
              <PersonIcon width={96} height={96} aria-hidden />
            </div>
          )}
        </div>

        <div className={styles.devices}>
          <DeviceButton
            isOn={audioEnabled}
            label={t(
              audioEnabled
                ? 'chat.call.toolbar.muteMicrophone'
                : 'chat.call.toolbar.unmuteMicrophone'
            )}
            onToggle={toggleMic}
            kind="audioinput"
            deviceId={audioDeviceId}
            warning={micWarning}
            onDeviceChange={setAudioDeviceId}
          />
          <DeviceButton
            isOn={videoEnabled}
            label={t(
              videoEnabled ? 'chat.call.toolbar.turnCameraOff' : 'chat.call.toolbar.turnCameraOn'
            )}
            onToggle={toggleCamera}
            kind="videoinput"
            deviceId={videoDeviceId}
            warning={cameraWarning}
            onDeviceChange={setVideoDeviceId}
          />
        </div>
      </div>

      <div className={styles.panel}>
        <h1 className={styles.heading}>{t('chat.call.prejoin.title')}</h1>

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.inputRow}>
            <input
              className={styles.input}
              type="text"
              name="username"
              placeholder={t('chat.call.prejoin.namePlaceholder')}
              autoComplete="off"
              maxLength={NAME_MAX_LENGTH}
              value={username}
              onChange={(event) => setUsername(event.target.value)}
            />
            <span className={styles.counter}>
              {username.length}/{NAME_MAX_LENGTH}
            </span>
          </div>
          <button type="submit" className={styles.joinButton} disabled={!username.trim()}>
            {t('chat.call.prejoin.joinButton')}
          </button>
        </form>

        {children}
      </div>
    </div>
  )
}
